import { NavLink, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";

/**
 * The admin screens are a console of their own, so they get their own
 * navigation instead of pushing seven more links into a top bar that already
 * carries the learner and instructor ones for the same person.
 */
export function AdminLayout() {
  // Email may be down, so the queue has to be visible in the product itself
  // rather than only announced by a message that never arrives.
  const { data: pending } = useQuery({
    queryKey: ["pending-instructor-count"],
    queryFn: () => apiGet<unknown[]>("/api/admin/instructors/pending"),
    refetchInterval: 60_000,
    retry: false,
  });

  // Whether the assessors are being compared decides whether the diagnostics
  // link is worth a slot. Asked of the admin endpoint itself: public health no
  // longer discloses configuration, so it never carried this flag.
  const { data: comparison } = useQuery({
    queryKey: ["grader-comparison-enabled"],
    queryFn: () => apiGet<{ enabled?: boolean }>("/api/admin/grader-comparisons?limit=1"),
    staleTime: 60_000,
    retry: false,
  });

  return (
    <div className="admin-layout">
      <nav className="admin-sidebar" aria-label="Admin sections">
        {/* `end` so Overview only lights up on /admin itself, not on every
            child route beneath it. */}
        <NavLink to="/admin" end>
          Overview
        </NavLink>
        <NavLink to="/admin/users">Users</NavLink>
        <NavLink to="/admin/instructors">
          Approvals
          {!!pending?.length && (
            <span className="admin-sidebar-count" aria-label={`${pending.length} waiting`}>
              {pending.length}
            </span>
          )}
        </NavLink>
        <NavLink to="/admin/learners">Learners</NavLink>
        <NavLink to="/admin/curricula">Teaching</NavLink>
        <NavLink to="/admin/reports">Reports</NavLink>
        {/* Only while both assessors are actually being run — a permanent link
            to an empty diagnostics table is clutter. */}
        {!!comparison?.enabled && (
          <NavLink to="/admin/grader-comparison">Assessors</NavLink>
        )}
      </nav>

      <div className="admin-content">
        <Outlet />
      </div>
    </div>
  );
}
